// src/components/UserProfileDropdown.js
import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  User, Settings, LogOut, Key, AlertCircle, CheckCircle, 
  Upload, Camera, Loader, X 
} from 'lucide-react';
import axios from 'axios';

const UserProfileDropdown = () => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showProfileImageModal, setShowProfileImageModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [profileImage, setProfileImage] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  
  const { currentUser, logout, changePassword, csrfToken, refreshCsrfToken, updateUserData } = useAuth();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const fileInputRef = useRef(null);
  
  // Cargar la imagen de perfil del usuario - MODIFICADO
  useEffect(() => {
    if (currentUser?.profileImage) {
      // Priorizar la imagen desde currentUser (base de datos)
      setProfileImage(currentUser.profileImage);
    } else {
      // Fallback a localStorage para retrocompatibilidad
      const savedProfileImage = localStorage.getItem('user-profile-image');
      if (savedProfileImage) {
        setProfileImage(savedProfileImage);
      }
    }
  }, [currentUser]);
  
  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  const handleLogout = async () => {
    const result = await logout();
    if (result.success) {
      navigate('/login');
    }
  };
  
  const handleChangePassword = async () => {
    // Validaciones
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('Por favor, completa todos los campos');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError('Las contraseñas nuevas no coinciden');
      return;
    }
    
    if (newPassword.length < 6) {
      setError('La nueva contraseña debe tener al menos 6 caracteres');
      return;
    }
    
    try {
      setError('');
      setSuccess('');
      setLoading(true);
      
      const result = await changePassword(currentPassword, newPassword);
      
      if (result.success) {
        setSuccess('Contraseña actualizada correctamente');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        
        // Cerrar el modal después de 2 segundos
        setTimeout(() => {
          setShowPasswordModal(false);
          setSuccess('');
        }, 2000);
      } else {
        setError(result.message);
      }
    } catch (error) {
      setError('Error al cambiar la contraseña');
    } finally {
      setLoading(false);
    }
  };
  
  // Función para subir la imagen de perfil - MODIFICADO
  const handleProfileImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validaciones básicas de tipo y tamaño de archivo
    if (!file.type.startsWith('image/')) {
      setError('Solo se permiten archivos de imagen');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) { // 5MB máximo
      setError('El tamaño máximo de la imagen es 5MB');
      return;
    }
    
    try {
      setError('');
      setSuccess('');
      setLoading(true);
      setUploadProgress(0);
      
      // Obtener token CSRF actualizado
      await refreshCsrfToken();
      
      // Crear el objeto FormData
      const formData = new FormData();
      formData.append('image', file);
      
      // Subir imagen al servidor
      const uploadResponse = await axios.post('/api/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-CSRF-Token': csrfToken
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });
      
      if (uploadResponse.data && uploadResponse.data.imageUrl) {
        // Guardar la URL de la imagen en la base de datos
        const updateResponse = await axios.post('/api/auth/update-profile-image', {
          profileImage: uploadResponse.data.imageUrl,
          _csrf: csrfToken
        }, {
          headers: {
            'X-CSRF-Token': csrfToken
          }
        });
        
        if (updateResponse.data) {
          // Actualizar estado local
          setProfileImage(uploadResponse.data.imageUrl);
          
          // Actualizar localStorage para retrocompatibilidad
          localStorage.setItem('user-profile-image', uploadResponse.data.imageUrl);
          
          // Actualizar el contexto de usuario con la nueva imagen
          updateUserData({ profileImage: uploadResponse.data.imageUrl });
          
          setSuccess('Imagen de perfil actualizada correctamente');
          
          // Cerrar el modal después de 2 segundos
          setTimeout(() => {
            setShowProfileImageModal(false);
            setSuccess('');
          }, 2000);
        }
      }
    } catch (error) {
      console.error('Error al subir la imagen:', error);
      
      // Manejar errores específicos
      if (error.response?.status === 403) {
        setError('Sesión expirada. Por favor, recarga la página e intenta de nuevo.');
      } else if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else {
        setError('Error al subir la imagen de perfil. Por favor, intenta de nuevo.');
      }
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };
  
  // Mostrar iniciales del usuario (como respaldo)
  const getUserInitials = () => {
    if (!currentUser || !currentUser.fullName) return 'U';
    
    const nameParts = currentUser.fullName.split(' ');
    if (nameParts.length >= 2) {
      return (nameParts[0][0] + nameParts[1][0]).toUpperCase();
    }
    return nameParts[0][0].toUpperCase();
  };
  
  return (
    <div className="relative" ref={dropdownRef}>
      <button
        className="bg-gray-700 flex text-sm rounded-full focus:outline-none"
        onClick={() => setShowDropdown(!showDropdown)}
      >
        {profileImage ? (
          <div className="h-8 w-8 rounded-full overflow-hidden">
            <img 
              src={profileImage} 
              alt="Perfil" 
              className="h-full w-full object-cover"
              onError={() => {
                // Si hay error al cargar la imagen, limpiar el estado y localStorage
                setProfileImage('');
                localStorage.removeItem('user-profile-image');
                // También actualizar la base de datos para limpiar la imagen rota
                axios.post('/api/auth/update-profile-image', {
                  profileImage: null,
                  _csrf: csrfToken
                }, {
                  headers: {
                    'X-CSRF-Token': csrfToken
                  }
                }).catch(err => console.error('Error al limpiar imagen rota:', err));
              }}
            />
          </div>
        ) : (
          <div className="h-8 w-8 rounded-full bg-purple-600 flex items-center justify-center">
            <span className="text-white font-bold">{getUserInitials()}</span>
          </div>
        )}
      </button>
      
      {showDropdown && (
        <div className="absolute right-0 mt-2 w-48 bg-gray-800 rounded-md shadow-lg z-50">
          <div className="border-b border-gray-700 p-3">
            <p className="text-white font-semibold">{currentUser?.fullName}</p>
            <p className="text-xs text-gray-400">{currentUser?.email}</p>
            <p className="text-xs text-purple-400 mt-1">{currentUser?.role === 'admin' ? 'Administrador' : 'Usuario'}</p>
          </div>
          
          <div className="py-1">
            <button 
              className="flex items-center w-full px-4 py-2 text-sm text-gray-300 hover:bg-gray-700"
              onClick={() => {
                setShowDropdown(false);
                setShowProfileImageModal(true);
              }}
            >
              <Camera size={16} className="mr-2" />
              Cambiar imagen de perfil
            </button>
            
            <button 
              className="flex items-center w-full px-4 py-2 text-sm text-gray-300 hover:bg-gray-700"
              onClick={() => {
                setShowDropdown(false);
                setShowPasswordModal(true);
              }}
            >
              <Key size={16} className="mr-2" />
              Cambiar contraseña
            </button>
            
            <button 
              className="flex items-center w-full px-4 py-2 text-sm text-gray-300 hover:bg-gray-700"
              onClick={handleLogout}
            >
              <LogOut size={16} className="mr-2" />
              Cerrar sesión
            </button>
          </div>
        </div>
      )}
      
      {/* Modal para cambiar contraseña */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 p-6 rounded-lg w-full max-w-md">
            <h2 className="text-xl font-bold mb-4 flex items-center">
              <Key size={20} className="text-purple-400 mr-2" />
              Cambiar Contraseña
            </h2>
            
            {error && (
              <div className="bg-red-900 bg-opacity-25 p-3 rounded-md mb-4 flex items-start">
                <AlertCircle className="text-red-500 mr-2 mt-0.5" size={16} />
                <p className="text-sm text-red-500">{error}</p>
              </div>
            )}
            
            {success && (
              <div className="bg-green-900 bg-opacity-25 p-3 rounded-md mb-4 flex items-start">
                <CheckCircle className="text-green-500 mr-2 mt-0.5" size={16} />
                <p className="text-sm text-green-500">{success}</p>
              </div>
            )}
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Contraseña Actual</label>
                <input 
                  type="password" 
                  className="w-full p-2 rounded-md bg-gray-700 border border-gray-600 text-white"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Nueva Contraseña</label>
                <input 
                  type="password" 
                  className="w-full p-2 rounded-md bg-gray-700 border border-gray-600 text-white"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Confirmar Nueva Contraseña</label>
                <input 
                  type="password" 
                  className="w-full p-2 rounded-md bg-gray-700 border border-gray-600 text-white"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <button 
                  type="button" 
                  className="px-4 py-2 border border-gray-600 rounded-md text-gray-300"
                  onClick={() => {
                    setShowPasswordModal(false);
                    setCurrentPassword('');
                    setNewPassword('');
                    setConfirmPassword('');
                    setError('');
                    setSuccess('');
                  }}
                >
                  Cancelar
                </button>
                <button 
                  type="button" 
                  className="px-4 py-2 bg-purple-600 rounded-md text-white"
                  onClick={handleChangePassword}
                  disabled={loading}
                >
                  {loading ? 'Actualizando...' : 'Actualizar Contraseña'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal para cambiar imagen de perfil */}
      {showProfileImageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 p-6 rounded-lg w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold flex items-center">
                <Camera size={20} className="text-purple-400 mr-2" />
                Cambiar Imagen de Perfil
              </h2>
              <button 
                className="text-gray-400 hover:text-white"
                onClick={() => {
                  setShowProfileImageModal(false);
                  setError('');
                  setSuccess('');
                }}
              >
                <X size={20} />
              </button>
            </div>
            
            {error && (
              <div className="bg-red-900 bg-opacity-25 p-3 rounded-md mb-4 flex items-start">
                <AlertCircle className="text-red-500 mr-2 mt-0.5" size={16} />
                <p className="text-sm text-red-500">{error}</p>
              </div>
            )}
            
            {success && (
              <div className="bg-green-900 bg-opacity-25 p-3 rounded-md mb-4 flex items-start">
                <CheckCircle className="text-green-500 mr-2 mt-0.5" size={16} />
                <p className="text-sm text-green-500">{success}</p>
              </div>
            )}
            
            <div className="space-y-4">
              {/* Vista previa de la imagen actual */}
              <div className="flex justify-center my-4">
                <div className="relative">
                  {profileImage ? (
                    <div className="h-24 w-24 rounded-full overflow-hidden border-4 border-gray-700">
                      <img 
                        src={profileImage} 
                        alt="Imagen de perfil" 
                        className="h-full w-full object-cover"
                        onError={() => {
                          setProfileImage('');
                          localStorage.removeItem('user-profile-image');
                        }}
                      />
                    </div>
                  ) : (
                    <div className="h-24 w-24 rounded-full bg-purple-600 flex items-center justify-center border-4 border-gray-700">
                      <span className="text-white text-3xl font-bold">{getUserInitials()}</span>
                    </div>
                  )}
                  
                  {/* Botón para cambiar la imagen */}
                  <button 
                    className="absolute bottom-0 right-0 bg-purple-600 rounded-full p-2 shadow-lg hover:bg-purple-700 transition-colors"
                    onClick={() => fileInputRef.current.click()}
                    disabled={loading}
                  >
                    <Camera size={16} className="text-white" />
                  </button>
                </div>
              </div>
              
              {/* Input de archivo oculto */}
              <input 
                type="file" 
                ref={fileInputRef} 
                className="hidden" 
                accept="image/*"
                onChange={handleProfileImageUpload}
                disabled={loading}
              />
              
              {/* Botón para seleccionar imagen */}
              <div className="flex justify-center">
                <button 
                  type="button" 
                  className="px-4 py-2 bg-gray-700 rounded-md text-white flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => fileInputRef.current.click()}
                  disabled={loading}
                >
                  <Upload size={16} />
                  {loading ? 'Subiendo...' : 'Seleccionar imagen'}
                </button>
              </div>
              
              {/* Barra de progreso de carga */}
              {loading && (
                <div className="w-full bg-gray-700 rounded-full h-2.5">
                  <div 
                    className="bg-purple-600 h-2.5 rounded-full transition-all duration-300" 
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              )}
              
              <p className="text-xs text-gray-400 text-center mt-2">
                Formatos permitidos: JPG, PNG, GIF. Tamaño máximo: 5MB
              </p>
              
              {/* Botón para eliminar imagen actual */}
              {profileImage && !loading && (
                <div className="flex justify-center mt-2">
                  <button
                    type="button"
                    className="px-3 py-1 bg-red-600 hover:bg-red-700 rounded-md text-white text-sm"
                    onClick={async () => {
                      try {
                        // Eliminar imagen de la base de datos
                        await axios.post('/api/auth/update-profile-image', {
                          profileImage: null,
                          _csrf: csrfToken
                        }, {
                          headers: {
                            'X-CSRF-Token': csrfToken
                          }
                        });
                        
                        // Actualizar estado local
                        setProfileImage('');
                        localStorage.removeItem('user-profile-image');
                        updateUserData({ profileImage: null });
                        
                        setSuccess('Imagen de perfil eliminada correctamente');
                      } catch (error) {
                        console.error('Error al eliminar imagen:', error);
                        setError('Error al eliminar la imagen de perfil');
                      }
                    }}
                  >
                    Eliminar imagen actual
                  </button>
                </div>
              )}
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <button 
                type="button" 
                className="px-4 py-2 border border-gray-600 rounded-md text-gray-300"
                onClick={() => {
                  setShowProfileImageModal(false);
                  setError('');
                  setSuccess('');
                }}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfileDropdown;
